import useGetList, {
  UseGetListOptions,
} from '@/service-library/core-hooks/useGetList'
import { getList } from '@/service-library/request-wrappers'
import {
  JobLiveListEntity,
  PhaseLiveListEntity,
  VendorLiveListEntity,
} from '@/types/live-lists'

type RelayLiveListHookOptions<T> = Omit<UseGetListOptions<T>, 'paginator'> & {
  orgId: string
  orgCredentialTypeId: string
  enabled?: boolean
}

function useGetRelayLiveListByPath<T>({
  path,
  orgId,
  orgCredentialTypeId,
  enabled = true,
  ...options
}: RelayLiveListHookOptions<T> & {
  path: string
}) {
  return useGetList<T>({
    queryKey: [
      'relay-live-list',
      path,
      options.filters,
      orgId,
      orgCredentialTypeId,
    ],
    paginator: 'page',
    serviceFn: ({ filters, ...serviceFnOptions }) => {
      return getList<T>({
        url: `/v2/relay/live_lists/${path}`,
        ...serviceFnOptions,
        filters: {
          ...filters,
          org_id: orgId,
          org_credential_type_id: orgCredentialTypeId,
        },
      })
    },
    enabled: !!(orgId && orgCredentialTypeId && enabled),
    ...options,
  })
}

export function useGetJobsLiveList<T = Record<string, unknown>>({
  ...options
}: RelayLiveListHookOptions<JobLiveListEntity<T>>) {
  const query = useGetRelayLiveListByPath<JobLiveListEntity<T>>({
    path: 'jobs',
    ...options,
  })

  return {
    jobs: query.allData,
    ...query,
  }
}

export function useGetPhasesLiveList<T = Record<string, unknown>>({
  jobNum,
  filters,
  enabled = true,
  ...options
}: RelayLiveListHookOptions<PhaseLiveListEntity<T>> & { jobNum: string }) {
  const query = useGetRelayLiveListByPath<PhaseLiveListEntity<T>>({
    path: 'phases',
    filters: {
      ...filters,
      job_num__eq: jobNum,
    },
    enabled: !!jobNum && enabled,
    ...options,
  })

  return {
    phases: query.allData,
    ...query,
  }
}

export function useGetVendorsLiveList<T = Record<string, unknown>>({
  ...options
}: RelayLiveListHookOptions<VendorLiveListEntity<T>>) {
  const query = useGetRelayLiveListByPath<VendorLiveListEntity<T>>({
    path: 'vendors',
    ...options,
  })

  return {
    vendors: query.allData,
    ...query,
  }
}
